import { formatDecimal } from '../../../lib/number/formatDecimal';

/**
 * Shared payslip value formatting — one origin for the amount format so the list
 * table and the detail view can never drift. Payslip values always show **2
 * decimals**, grouped per the **selected UI language** (`locale`, a BCP-47 tag
 * such as `fr` → space grouping + comma, `en` → comma grouping + dot) — never a
 * hardcoded locale. The currency comes from the synced data (`bulletin.devise`,
 * resolved from the terrain server-side) — no hardcoded default, appended as-is
 * and omitted when absent.
 */
export function formatPayslipNumber(value: number, locale: string): string {
  return formatDecimal(value, locale);
}

export function formatPayslipAmount(
  montant: number | null,
  currency: string | null,
  fallback: string,
  locale: string,
): string {
  if (montant == null) return fallback;
  const amount = formatPayslipNumber(montant, locale);
  return currency ? `${amount} ${currency}` : amount;
}

/**
 * Payroll month label `YYYYMM` → localized "full month + year", first letter
 * capitalized (e.g. `202607` → `July 2026` in `en`, `Juillet 2026` in `fr`).
 * Anything not matching `YYYYMM` is returned unchanged. Single origin so the
 * detail title and the frozen header show the same format. `locale` is the
 * selected UI language (BCP-47).
 */
export function formatPayslipPeriod(mois: string, locale: string): string {
  if (!/^\d{6}$/.test(mois)) return mois;
  const year = Number(mois.slice(0, 4));
  const month = Number(mois.slice(4, 6)); // 1-12
  // Reject out-of-range months (e.g. 202613 / 202600) instead of letting
  // Date.UTC roll them over into a different month/year — return unchanged.
  if (month < 1 || month > 12) return mois;
  const label = new Intl.DateTimeFormat(locale, {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(year, month - 1, 1)));
  return label.charAt(0).toUpperCase() + label.slice(1);
}
